import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    KEY_PRESS_FUNCTIONS, SHARED_CONSTANTS
} from '../../../../Shared/constants/commonConstants';
import { EnhancedSearchInputProps, SearchSuggestion } from '../interfaces/EnhancedSearchInterface';

const MAX_SUGGESTIONS_LIMIT = 10

export const useEnhancedSearch = ({
    value = '',
    handleOnChangeCb,
    suggestions = [],
    minCharactersForSuggestions = SHARED_CONSTANTS.MIN_CHARACTER_LIMIT_TO_TRIGGER_SEARCH,
    debounceMs = SHARED_CONSTANTS.SEARCH_DEBOUNCE_TIME,
}: EnhancedSearchInputProps) => {
    const [inputValue, setInputValue] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [filteredSuggestions, setFilteredSuggestions] = useState<
        SearchSuggestion[]
    >([]);
    const debounceRef = useRef<NodeJS.Timeout>();
    const containerRef = useRef<HTMLDivElement>(null);
    const isImmediateActionRef = useRef<boolean>(false);

    const getSuggestions = useMemo(() => {
        if (inputValue.length < minCharactersForSuggestions) return [];

        const searchTerm = inputValue.toLowerCase();
        const matches: SearchSuggestion[] = [];

        for (const suggestion of suggestions) {
            if (matches.length >= MAX_SUGGESTIONS_LIMIT) break;

            if (suggestion.value.toLowerCase().includes(searchTerm)) matches.push(suggestion);
        }
        return matches;
    }, [inputValue, suggestions, minCharactersForSuggestions]);

    useEffect(() => {
        setFilteredSuggestions(getSuggestions);
        setHighlightedIndex(-1);
    }, [getSuggestions]);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Debounced onChange for free-text search (when typing)
    useEffect(() => {
        if (isImmediateActionRef.current) {
            isImmediateActionRef.current = false;
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        // When typing, always pass undefined for suggestion (free-text search)
        debounceRef.current = setTimeout(() => handleOnChangeCb(inputValue, undefined), debounceMs);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [inputValue, handleOnChangeCb, debounceMs]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) setShowSuggestions(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
        // Note: Don't call handleOnChangeCb here - let debounce handle it for free-text search
        setShowSuggestions(
            newValue.length >= minCharactersForSuggestions &&
            filteredSuggestions.length > 0
        );
    };

    const handleInputFocus = () => {
        if (
            inputValue.length >= minCharactersForSuggestions &&
            filteredSuggestions.length > 0
        ) setShowSuggestions(true);

    };

    const handleSuggestionClick = (suggestion: SearchSuggestion) => {
        isImmediateActionRef.current = true;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setInputValue(suggestion.value);
        setShowSuggestions(false);
        // Pass full suggestion for field-specific filtering
        handleOnChangeCb(suggestion.value, suggestion);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!showSuggestions) {
            if (event.key === 'Enter') {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                // Enter without selecting suggestion = free-text search
                handleOnChangeCb(inputValue, undefined);
            }
            return;
        }

        switch (event.key) {
            case KEY_PRESS_FUNCTIONS.ARROW_DOWN:
                event.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredSuggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case KEY_PRESS_FUNCTIONS.ARROW_UP:
                event.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case KEY_PRESS_FUNCTIONS.ENTER:
                event.preventDefault();
                if (debounceRef.current) clearTimeout(debounceRef.current);
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < filteredSuggestions.length
                ) {
                    handleSuggestionClick(filteredSuggestions[highlightedIndex]);
                } else handleOnChangeCb(inputValue, undefined);

                setShowSuggestions(false);
                break;
            case KEY_PRESS_FUNCTIONS.ESCAPE:
                setShowSuggestions(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    const handleClear = () => {
        isImmediateActionRef.current = true;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setInputValue('');
        setShowSuggestions(false);
        // Clear search = clear field filter
        handleOnChangeCb('', undefined);
    };

    return {
        containerRef,
        inputValue,
        handleInputChange,
        handleInputFocus,
        handleKeyDown,
        handleClear,
        showSuggestions,
        filteredSuggestions,
        highlightedIndex,
        handleSuggestionClick,
    }
}
